import type { RouteObject } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import type {
  StoreSubscriptionPaymentReviewStatus,
  SubscriptionPlanType,
} from "@/lib/api/types";
import {
  STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS,
  SUBSCRIPTION_PLAN_TYPE,
} from "@/lib/api/types";
import { StoreSubscriptionsPage } from "./pages/StoreSubscriptionsPage";
import { WalletTopUpRequestsPage } from "./pages/WalletTopUpRequestsPage";
import { SubscriptionsPage } from "./pages/SubscriptionsPage";
import { useLocale } from "@admin/app/locale";

function toNumber(value: string | null, fallback: number) {
  const parsed = value ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
}

const PLAN_TYPES: readonly string[] = Object.values(SUBSCRIPTION_PLAN_TYPE);

/** A hand-edited query string must never reach the API as an unknown type. */
function toPlanTypeFilter(value: string | null): SubscriptionPlanType | "" {
  return value && PLAN_TYPES.includes(value)
    ? (value as SubscriptionPlanType)
    : "";
}

function AdminStoreSubscriptionsRoute() {
  const locale = useLocale();
  const [searchParams] = useSearchParams();

  return (
    <StoreSubscriptionsPage
      locale={locale}
      limit={toNumber(searchParams.get("limit"), 20)}
      offset={toNumber(searchParams.get("offset"), 0)}
      overdue={searchParams.get("overdue") === "true"}
      selectedPlanId={searchParams.get("planId")?.trim() || ""}
      selectedPlanType={toPlanTypeFilter(searchParams.get("planType"))}
    />
  );
}

const REVIEW_STATUSES: readonly string[] = Object.values(
  STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS,
);

/**
 * The queue an admin opens the screen for is the pending one, so an absent —
 * or hand-edited — `reviewStatus` falls back to PENDING rather than reaching
 * the API as an unknown string.
 */
function toReviewStatus(
  value: string | null,
): StoreSubscriptionPaymentReviewStatus {
  return value && REVIEW_STATUSES.includes(value)
    ? (value as StoreSubscriptionPaymentReviewStatus)
    : STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.PENDING;
}

function AdminWalletTopUpRequestsRoute() {
  const locale = useLocale();
  const [searchParams] = useSearchParams();

  return (
    <WalletTopUpRequestsPage
      locale={locale}
      reviewStatus={toReviewStatus(searchParams.get("reviewStatus"))}
      limit={toNumber(searchParams.get("limit"), 20)}
      offset={toNumber(searchParams.get("offset"), 0)}
      highlightedPaymentId={searchParams.get("paymentId")?.trim() || ""}
      highlightedStoreId={searchParams.get("storeId")?.trim() || ""}
    />
  );
}

function AdminSubscriptionsRoute() {
  const locale = useLocale();
  const [searchParams] = useSearchParams();

  return (
    <SubscriptionsPage
      locale={locale}
      searchParams={{
        active: searchParams.get("active") ?? undefined,
        inactive: searchParams.get("inactive") ?? undefined,
        paid: searchParams.get("paid") ?? undefined,
        free: searchParams.get("free") ?? undefined,
      }}
    />
  );
}

export const subscriptionRoutes: RouteObject[] = [
  {
    path: "stores/subscriptions",
    element: <AdminStoreSubscriptionsRoute />,
  },
  {
    path: "wallet-top-up-requests",
    element: <AdminWalletTopUpRequestsRoute />,
  },
  {
    path: "subscriptions",
    element: <AdminSubscriptionsRoute />,
  },
];
